// Typed fetch client + response shapes for the BetStock IA API.
import type {
  Product, Recommendation, DoNotReorder, PurchaseOrder, Alert, ExternalEvent,
  DashboardSummary, UnmetDemandSummary, Settings, ForecastPoint,
  ForecastMeta, OrderStatus, IngestResult, IngestRun, CombosResponse,
  ForecastResumen, ForecastSku,
  FichaProducto,
  Proveedor,
  ProveedoresResponse,
} from '@shared/types';

const BASE = '/api';

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { 'content-type': 'application/json' },
    body: body == null ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    // El server explica qué campo falló ("leadTimeDays: no puede ser negativo"). Sin
    // esto el usuario ve un 400 pelado y no sabe qué corregir.
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `${method} ${path} → ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ---- response envelopes ----
export interface ProductsResponse {
  items: Product[];
  total: number;
  totalValue: number;
  counts: Record<string, number>;
}
export interface RecommendationsResponse {
  items: Recommendation[];
  exclusions: DoNotReorder[];
  capital: number;
  counts: Record<string, number>;
}
export interface OrdersResponse {
  items: PurchaseOrder[];
  summary: { status: OrderStatus; count: number; totalCop: number }[];
  inProcess: number;
  total: number;
  notes: { ocNumber: string; note: string | null }[];
}
export interface AlertsResponse {
  items: Alert[];
  counts: { critico: number; alerta: number; info: number };
  total: number;
}
export interface EventsResponse {
  items: ExternalEvent[];
  counts: { total: number; activo: number; proximo: number; completado: number };
}
export interface ForecastResponse {
  points: ForecastPoint[];
  meta: ForecastMeta;
  resumen: ForecastResumen;
  items: ForecastSku[];
}
export interface ImportTemplate {
  title: string;
  description: string;
  columns: string[];
}
export type ImportTemplates = Record<string, ImportTemplate>;

export interface IngestStatus {
  last: IngestRun | null;
  /** false = falta el binding del B2B; el botón de actualizar no tiene de dónde leer. */
  b2bConectado: boolean;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

/** Campos de `products` que el admin edita a mano en Configuración. Todo opcional:
 *  lo que no viaja, no se toca (así la edición por lote puede mandar un solo campo). */
export interface ProductConfigPatch {
  unitCost?: number;
  leadTimeDays?: number;
  moq?: number;
  /**
   * Los dos overrides por producto. `null` es un valor con significado: **"volvé a
   * calcularlo"**. Sin él no habría forma de deshacer un valor fijado, que es exactamente lo
   * que hace falta el día que ese número envejeció.
   */
  safetyPct?: number | null;
  reorderPoint?: number | null;
}

export const api = {
  me: () => getJSON<{ authed: boolean; user?: SessionUser }>('/auth/me'),
  /** El mensaje del server se propaga tal cual: distingue "credenciales" de "cuenta sin acceso". */
  login: async (email: string, password: string): Promise<SessionUser> => {
    const res = await fetch(`${BASE}/auth/login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = (await res.json().catch(() => ({}))) as { user?: SessionUser; message?: string };
    if (!res.ok) throw new Error(data.message ?? 'No se pudo iniciar sesión.');
    return data.user!;
  },
  logout: () => send<{ ok: boolean }>('POST', '/auth/logout'),
  dashboard: () => getJSON<DashboardSummary>('/dashboard'),
  products: () => getJSON<ProductsResponse>('/products'),
  /** Combos y surtidos: van aparte de los productos, no tienen stock propio. */
  combos: () => getJSON<CombosResponse>('/combos'),
  recommendations: () => getJSON<RecommendationsResponse>('/recommendations'),
  /** Todo lo que el sistema sabe de un SKU, en una sola llamada. */
  ficha: (sku: string) => getJSON<FichaProducto>(`/products/${encodeURIComponent(sku)}/ficha`),
  approveRecommendation: (id: string) => send<{ ok: boolean }>('POST', `/recommendations/${id}/approve`),
  adjustRecommendation: (id: string, qty: number, note?: string) =>
    send<{ ok: boolean }>('POST', `/recommendations/${id}/adjust`, { qty, note }),
  approveRecommendationBatch: (ids: string[]) =>
    send<{ ok: boolean; aprobadas: number; salteadas: number; ocs: string[] }>(
      'POST', '/recommendations/approve-batch', { ids },
    ),
  suppliers: () => getJSON<ProveedoresResponse>('/suppliers'),
  createSupplier: (payload: Record<string, unknown>) => send<Proveedor>('POST', '/suppliers', payload),
  updateSupplier: (id: number, payload: Record<string, unknown>) =>
    send<Proveedor & { productosActualizados: number }>('PATCH', `/suppliers/${id}`, payload),
  deleteSupplier: (id: number) => send<{ ok: boolean }>('DELETE', `/suppliers/${id}`),
  assignSupplier: (id: number, skus: string[]) =>
    send<{ asignados: number; leadTimeDias: number | null }>('POST', `/suppliers/${id}/asignar`, { skus }),
  subirPlanilla: (filas: Record<string, unknown>[]) =>
    send<{ actualizados: number; noEncontrados: string[]; errores: { sku: string; motivo: string }[] }>(
      'POST', '/products/planilla', { filas },
    ),
  orders: () => getJSON<OrdersResponse>('/orders'),
  alerts: () => getJSON<AlertsResponse>('/alerts'),
  events: () => getJSON<EventsResponse>('/events'),
  createEvent: (payload: Partial<ExternalEvent>) => send<{ ok: boolean; id: string }>('POST', '/events', payload),
  unmetDemand: () => getJSON<UnmetDemandSummary>('/unmet-demand'),
  forecast: () => getJSON<ForecastResponse>('/forecast'),
  settings: () => getJSON<Settings>('/settings'),
  updateSettings: (payload: Partial<Settings>) => send<Settings>('PUT', '/settings', payload),
  /** Override por categoría. null en un campo = esa categoría vuelve al valor global. */
  updateCategorySetting: (payload: {
    category: string;
    serviceLevel: number | null;
    coverageTargetDays: number | null;
  }) => send<Settings>('PUT', '/settings/categoria', payload),
  /** Configuración editable de UN producto (costo unitario, lead time, MOQ). */
  updateProduct: (sku: string, payload: ProductConfigPatch) =>
    send<Product>('PATCH', `/products/${encodeURIComponent(sku)}`, payload),
  /** El mismo valor para varios SKU de una. */
  updateProductsBulk: (skus: string[], payload: ProductConfigPatch) =>
    send<{ updated: number; requested: number }>('PATCH', '/products', { skus, ...payload }),
  importTemplates: () => getJSON<ImportTemplates>('/import/templates'),
  /** Última actualización de datos: cuándo fue y qué trajo. */
  ingestStatus: () => getJSON<IngestStatus>('/ingest'),
  /** Dispara la ingesta desde el B2B. Tarda ~20 s: la UI tiene que mostrar que está trabajando. */
  runIngest: () => send<IngestResult>('POST', '/ingest/run'),
  /** Recalcula el forecast sin volver a ingerir (cambió el nivel de servicio o un lead time). */
  recalcularForecast: () => send<{ ok: boolean; detail: string }>('POST', '/forecast/recalcular'),
};
